import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { CheckCircle, AlertCircle, Loader2, ArrowRight, Plus, Eye, Copy, Check } from 'lucide-react';
import { useHinovaIntegration } from './hooks/useHinovaIntegration';
import { Client } from '@/types/client';
import { useToast } from '@/hooks/use-toast';
import api from '@/lib/api';
import MultiSelectDropdown from '@/components/ui/MultiSelectDropdown';
import PasswordConfirmationModal from '@/components/modals/PasswordConfirmationModal';
import { useAuth } from '@/contexts/AuthContext';

// Interface para a resposta da API de integração
interface IntegrationResponse {
  success: boolean;
  integration_id?: number;
  message: string;
  error?: string;
  token?: string;
}

interface HinovaIntegrationProps {
  client: Client;
}

const HinovaIntegration: React.FC<HinovaIntegrationProps> = ({ client }) => {
  const {
    step,
    selectedSystem,
    integrationActive,
    integrationData,
    tiposBoleto,
    situacoesVeiculo,
    selectedTiposBoleto,
    situacoesMapping,
    loading,
    saving,
    message,
    error,
    setSelectedSystem,
    setIntegrationData,
    startIntegration,
    authenticate,
    saveMapping,
    handleTipoBoletoChange,
    handleSituacaoMappingChange,
    clearMessages,
  } = useHinovaIntegration(client);

  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const { toast } = useToast();

  // Estados para consulta de token
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [decryptedToken, setDecryptedToken] = useState<string | null>(null);
  const [showToken, setShowToken] = useState(false);
  const [tokenCopied, setTokenCopied] = useState(false);

  // Logs de debug para verificar o estado
  console.log('🔍 [HinovaIntegration] Estado atual:', {
    clientId: client.id,
    step,
    selectedSystem,
    integrationActive,
    loading,
    saving
  });

  // Estado para controlar se as seções de mapeamento devem ser exibidas
  const [showMappingSections, setShowMappingSections] = useState(false);

  // Mapeamento de labels amigáveis para as situações
  const situacoesLabels: Record<string, string> = {
    'ativo': 'Ativo',
    'inadimplente_carencia': 'Inadimplente carência',
    'inadimplente_revistoria': 'Inadimplente fora da carência',
    'inadimplente_ate_30_dias': 'Todos até 30 dias',
    'inadimplente_ate_60_dias': 'Todos até 60 dias',
    'inadimplente_ate_90_dias': 'Todos até 90 dias',
    'inativo': 'Inativo',
    'cancelado': 'Cancelado'
  };

  // Limpar mensagens quando mudar de step
  useEffect(() => {
    clearMessages();
  }, [step]);

  // Mostrar toast quando houver mensagem
  useEffect(() => {
    if (message) {
      toast({
        title: "Sucesso!",
        description: message,
      });
    }
  }, [message, toast]);

  useEffect(() => {
    if (error) {
      toast({
        title: "Erro",
        description: error,
        variant: "destructive"
      });
    }
  }, [error, toast]);

  const handleStartIntegration = () => {
    const success = startIntegration();
    if (!success) {
      toast({
        title: "Sistema não selecionado",
        description: "Por favor, selecione um sistema de gestão",
        variant: "destructive"
      });
    }
  };

  const handleAuthenticate = async () => {
    const success = await authenticate();
    if (!success) {
      toast({
        title: "Erro na autenticação",
        description: error,
        variant: "destructive"
      });
    }
  };

  const handleSaveMapping = async () => {
    const success = await saveMapping();
    if (!success) {
      toast({
        title: "Erro ao salvar",
        description: error,
        variant: "destructive"
      });
    }
  };

  const handleNewIntegration = async () => {
    console.log('🎯 FUNÇÃO CHAMADA! handleNewIntegration executada');
    try {
      console.log('🔄 Iniciando processo de nova integração...');
      console.log('📋 Dados do cliente:', { id: client.id, selectedSystem });

      const payload = {
        client_id: client.id,
        system_type: selectedSystem || 'hinova',
        system_url: 'https://placeholder.com', // URL placeholder válida
        token: 'placeholder_token', // Token placeholder válido
        username: 'placeholder_user', // Username placeholder válido
        password: 'placeholder_pass', // Password placeholder válido
        user_token: '',
        is_active: false // Desativar a integração
      };

      console.log('📤 Enviando payload:', payload);

      // Verificar se o usuário está autenticado
      console.log('🔐 Verificando autenticação...');
      try {
        const meResponse = await api.get('/auth/me');
        console.log('✅ Usuário autenticado:', meResponse);
      } catch (authError) {
        console.error('❌ Erro de autenticação:', authError);
        throw new Error('Usuário não autenticado');
      }

      // Como não temos um endpoint DELETE direto, vamos usar uma abordagem alternativa
      // Vamos simular a remoção da integração atualizando para um estado "limpo"
      // mas com campos válidos para passar na validação
      console.log('📡 Fazendo requisição POST para /integrations/save...');
      const response = await api.post<IntegrationResponse>('/integrations/save', payload);

      console.log('📥 Resposta recebida:', response);

      if (response.success) {
        console.log('✅ Integração desativada com sucesso!');
        toast({
          title: "Integração removida",
          description: "A integração atual foi removida. Configure uma nova integração.",
        });

        // Resetar todos os estados para voltar ao estado inicial
        setShowMappingSections(false);
        // Recarregar a página para resetar todos os estados
        console.log('🔄 Recarregando página...');
        window.location.reload();
      } else {
        console.log('❌ Resposta não indica sucesso:', response);
        throw new Error(response.error || 'Erro ao remover integração');
      }
    } catch (error: any) {
      console.error('❌ Erro ao remover integração:', error);
      console.error('📋 Detalhes do erro:', {
        message: error.message,
        response: error.response,
        status: error.response?.status,
        data: error.response?.data
      });
      toast({
        title: "Erro ao remover integração",
        description: error.response?.data?.error || error.message || "Erro interno do servidor",
        variant: "destructive"
      });
    }
  };

  // Funções para consultar token
  const handleOpenTokenModal = () => {
    setIsPasswordModalOpen(true);
    setDecryptedToken(null);
    setShowToken(false);
  };

  const handlePasswordConfirm = async (password: string) => {
    try {
      // Como não temos acesso direto ao ID da integração aqui, vamos buscar a integração do cliente
      // e depois, se encontrarmos, chamamos a rota de descriptografar

      // 1. Buscar integração ativa do cliente para pegar o ID
      // Definir tipo inline para resposta de integração
      const integrationResponse = await api.get<{ integration: any }>(`/integrations/client/${client.id}`);
      const integration = integrationResponse.integration;

      if (!integration) {
        throw new Error('Integração não encontrada');
      }

      // 2. Chamar o backend para verificar senha e descriptografar
      const response = await api.post<IntegrationResponse>('/integrations/decrypt-token', {
        password,
        integration_id: integration.id
      });

      if (response.success && response.token) {
        setDecryptedToken(response.token);
        setShowToken(true);
        setIsPasswordModalOpen(false);

        toast({
          title: "Token recuperado",
          description: "Token descriptografado com sucesso.",
        });
      } else {
        throw new Error(response.error || 'Erro ao recuperar token');
      }
    } catch (error: any) {
      console.error('Erro ao consultar token:', error);
      const errorMessage = error.response?.data?.error || error.message || "Senha incorreta ou erro no servidor";
      throw new Error(errorMessage);
    }
  };

  const copyTokenToClipboard = () => {
    if (decryptedToken) {
      navigator.clipboard.writeText(decryptedToken);
      setTokenCopied(true);
      setTimeout(() => setTokenCopied(false), 2000);
      toast({
        title: "Copiado!",
        description: "Token copiado para a área de transferência.",
      });
    }
  };

  return (
    <div className="space-y-6">
      {/* Modal de Confirmação de Senha */}
      <PasswordConfirmationModal
        isOpen={isPasswordModalOpen}
        onClose={() => setIsPasswordModalOpen(false)}
        onConfirm={handlePasswordConfirm}
        title="Consultar Token"
        description="Por segurança, confirme sua senha para visualizar o token de integração."
      />

      {/* Status da Integração */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              {integrationActive ? (
                <>
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  Integração Ativa
                </>
              ) : (
                <>
                  <AlertCircle className="w-5 h-5 text-yellow-600" />
                  Integração Pendente
                </>
              )}
            </CardTitle>

            {/* Botões de ação quando integração está ativa */}
            {integrationActive && (
              <div className="flex gap-2">
                {isAdmin && (
                  <Button
                    onClick={handleOpenTokenModal}
                    variant="outline"
                    size="sm"
                    className="flex items-center gap-2"
                  >
                    <Eye className="w-4 h-4" />
                    Consultar Token
                  </Button>
                )}

                <Button
                  onClick={() => {
                    console.log('🔘 BOTÃO CLICADO! Nova integração');
                    handleNewIntegration();
                  }}
                  variant="outline"
                  size="sm"
                  className="flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Nova integração
                </Button>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {integrationActive ? (
            <div className="space-y-4">
              <p className="text-green-600">Sistema conectado e funcionando</p>

              {/* Exibição do Token Descriptografado */}
              {showToken && decryptedToken && isAdmin && (
                <div className="mt-4 p-4 bg-gray-50 border border-gray-200 rounded-md animate-in fade-in slide-in-from-top-2">
                  <Label className="text-sm font-medium text-gray-500 mb-2 block">Token de Integração (Descriptografado)</Label>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 bg-white p-3 rounded border font-mono text-sm break-all">
                      {decryptedToken}
                    </code>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={copyTokenToClipboard}
                      className="shrink-0"
                      title="Copiar token"
                    >
                      {tokenCopied ? (
                        <Check className="w-4 h-4 text-green-600" />
                      ) : (
                        <Copy className="w-4 h-4" />
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setShowToken(false)}
                      className="shrink-0"
                      title="Ocultar"
                    >
                      <Eye className="w-4 h-4 text-gray-400" />
                    </Button>
                  </div>
                  <p className="text-xs text-amber-600 mt-2 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    Este token é sensível. Não compartilhe com terceiros não autorizados.
                  </p>
                </div>
              )}
            </div>
          ) : (
            <p className="text-gray-600">Configure a integração para começar</p>
          )}
        </CardContent>
      </Card>


      {/* Passo 1: Seleção do Sistema */}
      {step === 'initial' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <span className="bg-blue-100 text-blue-600 rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">1</span>
              Selecione o Sistema de Gestão
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="gestion-system">Sistema de Gestão</Label>
              <select
                id="gestion-system"
                value={selectedSystem}
                onChange={(e) => setSelectedSystem(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Selecione o sistema</option>
                {client.system_management === 'hinova' && <option value="hinova">Hinova</option>}
                {client.system_management === 'south' && <option value="south">South</option>}
                {client.system_management === 'south-v0' && <option value="south-v0">South V0</option>}
                {client.system_management === 'siprove' && <option value="siprove">Siprove</option>}
                {client.system_management === 'epta' && <option value="epta">Epta</option>}
                {/* Fallback: se não houver system_management definido, mostrar todas as opções */}
                {!client.system_management && (
                  <>
                    <option value="hinova">Hinova</option>
                    <option value="south">South</option>
                    <option value="south-v0">South V0</option>
                    <option value="siprove">Siprove</option>
                    <option value="epta">Epta</option>
                  </>
                )}
              </select>
            </div>

            <Button
              onClick={handleStartIntegration}
              className="bg-blue-600 hover:bg-blue-700 px-6 py-3"
              disabled={!selectedSystem}
            >
              Iniciar Integração
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Passo 2: Credenciais */}
      {step === 'credentials' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <span className="bg-blue-100 text-blue-600 rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">2</span>
              Credenciais de Acesso
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="token">Token</Label>
              <Input
                id="token"
                type="password"
                placeholder="Bearer token gerado pelo SGA"
                value={integrationData.token}
                onChange={(e) => setIntegrationData(prev => ({ ...prev, token: e.target.value }))}
                className="p-3"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="usuario">Usuário</Label>
                <Input
                  id="usuario"
                  placeholder="Usuário ativo do sistema"
                  value={integrationData.usuario}
                  onChange={(e) => setIntegrationData(prev => ({ ...prev, usuario: e.target.value }))}
                  className="p-3"
                />
              </div>
              <div>
                <Label htmlFor="senha">Senha</Label>
                <Input
                  id="senha"
                  type="password"
                  placeholder="Senha de acesso"
                  value={integrationData.senha}
                  onChange={(e) => setIntegrationData(prev => ({ ...prev, senha: e.target.value }))}
                  className="p-3"
                />
              </div>
            </div>

            <div className="flex items-center gap-4 pt-4">
              <Button
                onClick={handleAuthenticate}
                disabled={loading || !integrationData.token || !integrationData.usuario || !integrationData.senha}
                className="bg-blue-600 hover:bg-blue-700 px-6 py-3"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Conectando...
                  </>
                ) : (
                  'Conectar e Salvar'
                )}
              </Button>
              <p className="text-sm text-gray-600">
                Preencha token, usuário, senha para iniciar.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Passo 3: Mapeamentos */}
      {(step === 'mapping' || integrationActive) && (
        <div className="space-y-6">
          {/* Mapeamento de Tipos de Boleto */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <span className="bg-blue-100 text-blue-600 rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">3</span>
                Mapeamento - Tipos de Boleto
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <p className="text-sm text-gray-600">
                  Selecione os tipos de boleto que devem ser processados:
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-60 overflow-y-auto border border-gray-200 rounded-md p-4">
                  {tiposBoleto.length > 0 ? (
                    tiposBoleto.map((tipo, index) => {
                      // Debug: log da estrutura do tipo
                      console.log(`🔍 Tipo ${index}:`, tipo);

                      // Usar um identificador único baseado em múltiplos campos
                      const uniqueId = tipo.codigo || tipo.id || tipo.descricao || `tipo-${index}`;

                      return (
                        <div key={uniqueId} className="flex items-center space-x-3 p-2 hover:bg-gray-50 rounded">
                          <input
                            type="checkbox"
                            id={`boleto-${uniqueId}`}
                            checked={selectedTiposBoleto.includes(uniqueId)}
                            onChange={(e) => {
                              console.log(`🔘 Checkbox alterado: ${uniqueId} = ${e.target.checked}`);
                              handleTipoBoletoChange(uniqueId, e.target.checked);
                            }}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <Label htmlFor={`boleto-${uniqueId}`} className="text-sm cursor-pointer">
                            {tipo.descricao || tipo.descricao_tipo_boleto || tipo.nome || tipo.label || JSON.stringify(tipo)}
                          </Label>
                        </div>
                      );
                    })
                  ) : loading ? (
                    <p className="text-gray-500 text-center py-4">Carregando tipos de boleto...</p>
                  ) : error ? (
                    <div className="text-red-500 text-center py-4">
                      <p className="font-medium">Erro ao carregar tipos de boleto:</p>
                      <p className="text-sm">{error}</p>
                    </div>
                  ) : (
                    <p className="text-gray-500 text-center py-4">Nenhum tipo de boleto encontrado</p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Situações de Veículo Disponíveis */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <span className="bg-green-100 text-green-600 rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">4</span>
                Situações de Veículo Disponíveis
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <p className="text-sm text-gray-600">
                  Situações disponíveis no sistema Hinova:
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-60 overflow-y-auto border border-gray-200 rounded-md p-4">
                  {situacoesVeiculo.length > 0 ? (
                    situacoesVeiculo.map((situacao, index) => (
                      <div key={index} className="flex items-center space-x-3 p-2 hover:bg-gray-50 rounded">
                        <div className="w-4 h-4 bg-gray-200 rounded flex-shrink-0"></div>
                        <span className="text-sm text-gray-700">
                          {situacao.descricao || situacao.descricao_situacao || situacao.nome || situacao.label || JSON.stringify(situacao)}
                        </span>
                      </div>
                    ))
                  ) : loading ? (
                    <p className="text-gray-500 text-center py-4">Carregando situações de veículo...</p>
                  ) : error ? (
                    <div className="text-red-500 text-center py-4">
                      <p className="font-medium">Erro ao carregar situações de veículo:</p>
                      <p className="text-sm">{error}</p>
                    </div>
                  ) : (
                    <p className="text-gray-500 text-center py-4">Nenhuma situação de veículo encontrada</p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Mapeamento de Situações de Veículo */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <span className="bg-purple-100 text-purple-600 rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">5</span>
                Mapeamento - Situações de Veículo
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <p className="text-sm text-gray-600">
                  Relacione as situações do sistema com as nossas categorias:
                </p>

                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-1/3">Nossa Situação</TableHead>
                      <TableHead>Situação no Sistema</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {Object.entries(situacoesMapping).map(([situacao, valor]) => (
                      <TableRow key={situacao}>
                        <TableCell>
                          <Badge variant="outline" className="text-sm">
                            {situacoesLabels[situacao] || situacao}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="w-full max-w-md">
                            {situacoesVeiculo.length === 0 ? (
                              <p className="text-sm text-muted-foreground">Nenhuma situação disponível</p>
                            ) : (
                              <MultiSelectDropdown
                                options={situacoesVeiculo
                                  .filter((situacao, index) => {
                                    // 🔧 VALIDAÇÃO CRÍTICA: Filtrar apenas objetos válidos
                                    const isValid = situacao && typeof situacao === 'object';
                                    if (!isValid) {
                                      console.error(`❌ [HinovaIntegration] Situação inválida no índice ${index}:`, situacao);
                                    }
                                    return isValid;
                                  })
                                  .map((situacao, index) => {
                                    console.log(`🔍 [HinovaIntegration] Processando situação ${index}:`, situacao);

                                    // 🔧 VALIDAÇÃO ROBUSTA: Garantir que sempre temos uma string válida
                                    let situacaoDesc = '';

                                    try {
                                      // Tentar extrair a descrição de forma segura
                                      const rawDesc = situacao?.descricao || situacao?.descricao_situacao || situacao?.nome || situacao?.label;

                                      // Converter para string e validar
                                      if (rawDesc !== null && rawDesc !== undefined) {
                                        situacaoDesc = String(rawDesc).trim();
                                      }

                                      // Se ainda estiver vazio, usar fallback
                                      if (!situacaoDesc || situacaoDesc === '') {
                                        situacaoDesc = `Situação ${index + 1}`;
                                      }
                                    } catch (error) {
                                      console.error(`❌ [HinovaIntegration] Erro ao processar descrição da situação ${index}:`, error);
                                      situacaoDesc = `Situação ${index + 1}`;
                                    }

                                    // 🔧 GARANTIR VALOR ÚNICO E VÁLIDO (SEM PREFIXO)
                                    const safeValue = situacaoDesc.replace(/[^a-zA-Z0-9]/g, '_');

                                    const option = {
                                      value: safeValue,
                                      label: situacaoDesc,
                                      originalData: situacaoDesc // Manter referência ao valor original
                                    };

                                    console.log(`🔍 [HinovaIntegration] Opção criada:`, option);
                                    return option;
                                  })}
                                value={Array.isArray(valor) ? valor : []}
                                onChange={(newValues) => {
                                  handleSituacaoMappingChange(situacao as any, newValues);
                                }}
                                placeholder="Selecione as situações correspondentes"
                              />
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>

          {/* Botão Salvar */}
          <div className="flex justify-end">
            <Button
              onClick={handleSaveMapping}
              disabled={saving}
              className="bg-green-600 hover:bg-green-700 px-6 py-3"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Salvando...
                </>
              ) : (
                'Salvar Mapeamentos'
              )}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default HinovaIntegration;
